import { Tooltip, TooltipTrigger, WithNAFallback } from '@toggle/design-system';
import { MappedEntity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getPriceStamp } from '~/components/price-display-view/helpers/price-display-helpers';
import { Identifier } from '~/hooks/use-live-price';
import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';

import { PriceCellTooltip } from './price-cell-tooltip/PriceCellTooltip';
import * as S from './PriceCell.styles';

interface PriceCellProps {
  isEditMode?: boolean;
  asset?: MappedEntity;
}

export const PriceCell = ({ isEditMode = false, asset }: PriceCellProps) => {
  const { t } = useTranslation(['widget', 'watchlistManager']);

  const { price, date, isLive, isSubscribing, isLivePriceSupported } =
    useOneDayReturnLivePrice(asset, Identifier.Watchlist);

  const hasData = !!asset && !!price;

  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled: isEditMode,
  });

  if (isSubscribing) {
    return <S.StyledSkeletonLoader />;
  }

  return (
    <WithNAFallback
      check={hasData}
      tooltip={t('widget:watchlist.noLivePriceDataAvailable')}
      value={
        hasData && (
          <Tooltip
            isTouchDevice={false}
            trigger={TooltipTrigger.Hover}
            label={
              date && <PriceCellTooltip type={stamp.stampType} date={date} />
            }
            inPortal
            disabled={!date}
          >
            <S.FlexWrapper>
              <S.PriceWrapper $isDisabled={isEditMode}>
                <span>
                  {globalPriceFormatter({
                    price,
                    entity: asset,
                    priceSuffix: '',
                  })}
                </span>
                <S.Currency>{asset.currency}</S.Currency>
              </S.PriceWrapper>
              <S.StampWrapper>
                {stamp.stampType === 'notSupported' ? (
                  <S.SquareStamp $color={stamp.color} />
                ) : (
                  <S.CircleStamp $color={stamp.color} />
                )}
              </S.StampWrapper>
            </S.FlexWrapper>
          </Tooltip>
        )
      }
    />
  );
};
